import {
  DeadLetterDetail,
  DeadLetterDetailFromJSON,
  DeadLetterFilters,
  DeadLetterPage,
  DeadLetterPageFromJSON,
  localInputToUtcInstant,
} from "../model/dead-letter";

const BASE_URL = "/api/pubsub-ingester-service/pubsub";

/**
 * Carries the HTTP status so the page can tell a missing token (401) from a
 * missing ADMIN role (403), a resend the backend refuses (400/409) or a plain
 * outage.
 */
export class DeadLetterApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "DeadLetterApiError";
    Object.setPrototypeOf(this, DeadLetterApiError.prototype);
  }

  get unauthorized(): boolean {
    return this.status === 401;
  }

  get forbidden(): boolean {
    return this.status === 403;
  }
}

export interface GetDeadLettersRequestParams {
  pageNumber: number;
  pageSize: number;
  filters: Partial<DeadLetterFilters>;
}

export function buildDeadLettersQuery(
  requestParams: GetDeadLettersRequestParams
): string {
  const { pageNumber, pageSize, filters } = requestParams;
  const result = [
    `page=${encodeURIComponent(pageNumber)}`,
    `size=${encodeURIComponent(pageSize)}`,
  ];
  const textFilters: (keyof DeadLetterFilters)[] = [
    "originalTopic",
    "tenantName",
    "eventId",
    "objectId",
    "q",
  ];
  textFilters.forEach((key) => {
    const value = filters[key]?.trim();
    if (value) {
      result.push(`${key}=${encodeURIComponent(value)}`);
    }
  });
  const from = localInputToUtcInstant(filters.from ?? "");
  if (from) {
    result.push(`from=${encodeURIComponent(from)}`);
  }
  const to = localInputToUtcInstant(filters.to ?? "");
  if (to) {
    result.push(`to=${encodeURIComponent(to)}`);
  }
  return result.join("&");
}

async function readErrorMessage(response: Response): Promise<string> {
  // A 403 from the gateway or a 409 from the service may have an empty or
  // non-JSON body; fall back to the status rather than failing on parse.
  try {
    const text = await response.text();
    if (!text) {
      return `Request failed with status ${response.status}`;
    }
    try {
      const json = JSON.parse(text);
      return json.message || json.error || text;
    } catch {
      return text;
    }
  } catch {
    return `Request failed with status ${response.status}`;
  }
}

/** The part of the Keycloak adapter this client needs. */
export interface TokenSource {
  token?: string;
  updateToken: (minValidity: number) => PromiseLike<boolean>;
}

/**
 * Sends the Keycloak token itself instead of relying on the shared fetch
 * interceptor. That interceptor only adds it for the prefixes listed in each
 * tenant's frontend.routesWithAuthTokenMap.monitoring, and pubsub-ingester-service
 * is not listed there: this internal monitoring page is LikeMagic-only, so the
 * route is not configured on every tenant. A backoffice version of this page
 * would need the route added to the tenants' configuration instead.
 */
class DeadLetterApiClient {
  private keycloak?: TokenSource;

  setKeycloak(keycloak?: TokenSource) {
    this.keycloak = keycloak;
  }

  private async authHeader(): Promise<Record<string, string>> {
    const keycloak = this.keycloak;
    if (!keycloak?.token) {
      return {};
    }
    try {
      await keycloak.updateToken(5);
    } catch {
      // A failed refresh goes out without a token; the 401 tells the page to
      // ask for a new login.
      return {};
    }
    return keycloak.token ? { Authorization: `Bearer ${keycloak.token}` } : {};
  }

  private async request(input: string, init?: RequestInit): Promise<Response> {
    const response = await fetch(input, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(await this.authHeader()),
        ...init?.headers,
      },
    });
    if (!response.ok) {
      throw new DeadLetterApiError(
        response.status,
        await readErrorMessage(response)
      );
    }
    return response;
  }

  async getDeadLetters(
    requestParams: GetDeadLettersRequestParams
  ): Promise<DeadLetterPage> {
    const response = await this.request(
      `${BASE_URL}/dead-letters?${buildDeadLettersQuery(requestParams)}`
    );
    return DeadLetterPageFromJSON(await response.json());
  }

  async getDeadLetter(id: number): Promise<DeadLetterDetail> {
    const response = await this.request(
      `${BASE_URL}/dead-letters/${encodeURIComponent(id)}`
    );
    return DeadLetterDetailFromJSON(await response.json());
  }

  async resend(id: number): Promise<boolean> {
    const response = await this.request(
      `${BASE_URL}/resend/${encodeURIComponent(id)}`,
      { method: "POST" }
    );
    return (await response.json()) === true;
  }
}

export const DeadLetterApi = new DeadLetterApiClient();
